import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	LookupInput,
	FormItem,
	Form,
	NumberUtils,
	Icon,
	PopupInput,
	NumberRangeInput,
	TextInput2,
	ParentBoundComponent
} from 'ps-gui';

import DashboardModel from './dashboardmodel';
import {
	getColumnText,
	getPhrase
} from '../data/phrases';

import { getLookup } from '../data/lookup';

import {
	GlobalContext,
	withGlobalContext
} from '../data/globalcontext';
import Message from '../blocks/message';
import {
	CellBubble,
	CellMainText
} from './portalresultsetmodule';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock
} from '../blocks/modulesections';
import { ReactComponent as ModuleLogo } from '../svg/dashboard.svg';
import { getGlobalContextChangeChecker } from './utils';

const Wrapper = styled.div`
`;

const SummaryBlock = styled.div`
	display: flex;
	padding: 32px 0px;
	gap: 30px;
`;

const SummaryItem = styled.div`
	flex-basis: 0px;
	flex: 1 1 0px;
	padding: 34px 20px;
	border-radius: 16px;
	overflow: hidden;
	background: #FFFFFF;
	box-shadow: -4px 2px 33px 3px rgba(21, 46, 67, 0.1);
	color: #575757;
	border-radius: 15px;
	> .label {
		font-size: 16px;
		font-weight: 500;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	> .value {
		margin-top: 16px;
		font-size: 26px;
		font-weight: 700;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;

		.big {
			font-size: 28px;
		}
		.small {
			font-size: 16px;
		}
	}
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);
const EMPTY_SET = Immutable.Set([]);

const SmartTable = styled.table`
	width: 100%;
	z-index: 0;
	border-spacing: 0px;
	table-layout: fixed;
	td, th {
		&.right {
			text-align: right !important;
		}
	}
	thead {
		tr {
			&:first-child {
				th:first-child {
					border-top-left-radius: 15px;
				}
				th:last-child {
					border-top-right-radius: 15px;
				}
			}

			&.dummyHeaderRow {
				height: 8px;
				th {
					padding: 0px;
					border: none;
					background: #EAF0F2;
				}
			}
			th {
				z-index: 3;
				text-align: left;
				padding: 12px;
				background: ${props => props.theme.tableHeaderBackground};
				border-right: 1px solid ${props => props.theme.tableCellBorderColor};
				box-shadow: ${props => props.theme.tableHeaderShadow};
				> .text {
					font-size: 14px;
					font-weight: 500;
					z-index: 1;
					position: relative;
					padding-right: 20px;
					color: ${props => props.theme.tableHeaderTextColor};
				}
				.subText {
					color: ${props => props.theme.disabledTextColor};
					font-size: 14px;
					font-weight: 300;
				}
				&.sortable {
					> .text {
						cursor: pointer;
						&:hover {
							text-decoration: underline;
						}
					}
				}

				.sortIcons {
					position: absolute;
					right: 0px;
					top: calc( 50% - 10px );

					.icon.selected {
						* {
							fill: ${props => props.theme.activeColor};
						}
					}
				}
			}
			&.sticky {
				position: sticky;
				top: 0;
			}
			&.filter {
				th {

					border-bottom: 1px solid ${props => props.theme.tableCellBorderColor};
				}
			}
		}
	}
	tbody {
		tr {
			cursor: default;
			background: ${props => props.theme.tableRowBackground};
			&:not(.noHover):hover {
				background: ${props => props.theme.tableRowHoverBackground};
			}
			&.alt {
				background: ${props => props.theme.tableRowAltBackground};
			}
			td {
				padding: 8px 12px;
				font-size: 14px;
				font-weight: 400;
				border-right: 1px solid ${props => props.theme.tableCellBorderColor};
				border-bottom: 1px solid ${props => props.theme.tableCellBorderColor};
				&.expansion {
					width: 1px;
					border-right: none;
					.icon {
						opacity: 1;
						&.disabled {
							visibility: hidden;
						}
					}
				}
				&.dummy {
					width: 1px;
					border-right: none;
					border-bottom: none;
				}
				&.subHeader {
					font-size: 14px;
					font-weight: 400;
					padding-left: 0px;
					background: #F8F8F8;
				}
				&.nodeTextCell {
					padding-left: 0px;
				}
			}
		}
	}
`;

const YearCell = styled.span`
	background: #F3FAFD;
	display: inline;
	border: 1px solid #306DA0;
	padding: 2px 10px;
	border-radius: 16px;
	font-size: 14px;
	font-weight: 500;
	line-height: 28px;
	:not(:last-child) {
		margin-right: 6px;
	}
`;

const Filter = styled.div`
	margin-top: 8px;
	.ps-input {
		width: 100%;
	}
`;

class DashboardModule extends ParentBoundComponent {
	constructor() {
		super();
		this.setRoleId = this.setRoleId.bind(this);
		this.setDistributionPeriodId = this.setDistributionPeriodId.bind(this);
		this.formatAmount = NumberUtils.getFixedDecimalFormatNumber(2);
		this.formatWholeNumber = NumberUtils.getFixedDecimalFormatNumber(0);

		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);

	}

	setFilterItem(name, value) {
		this.setModel(this.getModel().setFilterItem(name, value));
	}

	async setRoleId(value) {
		let model = this.getModel().setRoleId(value);
		this.setModel(await model.loadData());
	}

	async setDistributionPeriodId(value) {
		let model = this.getModel().setDistributionPeriodId(value);
		this.setModel(await model.loadData());
	}

	orderBy(name, desc) {
		this.setModel(this.getModel().orderBy(name, desc));
	}

	getModel() {
		return new DashboardModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	renderExpansionIcon(id, expanded, disabled) {
		return (
			<Icon
				name={expanded ? 'collapse' : 'expand'}
				active
				disabled={disabled}
				onMouseDown={e => {
					this.setModel(this.getModel().toggleExpandedRow(id));
				}}
			/>
		);
	}

	appendRow(res, row, alt, level = 0, expandedRows) {
		const id = row.get('id');
		const distType = row.get('distType');
		const kids = row.get('children');
		const expanded = expandedRows.has(id);
		res.push(
			<tr key={id} className={alt ? 'alt' : ''}>
				<td className={level === 0 ? 'expansion' : 'dummy'}>
					{
						level === 0 && this.renderExpansionIcon(id, expanded, !kids)
					}
				</td>
				{
					level > 0 &&
					<td className={level === 1 ? 'expansion' : 'dummy'}>
						{
							level === 1 && this.renderExpansionIcon(id, expanded, !kids)
						}
					</td>

				}
				{
					level > 1 &&
					<td className="expansion">
						<Icon
							name="expand"
							disabled
						/>
					</td>

				}
				<td colSpan={3 - level} className="nodeTextCell">
					{row.get('categoryName')}
				</td>
				<td>{(row.get('effectivePeriodId') || EMPTY_LIST).map((dp, index) => {
					return (
						<YearCell key={index}> {dp} </YearCell>
					);
				})}</td>
				<td className="right"><CellBubble className={distType}>{getPhrase(distType)}</CellBubble></td>
				<td className="right">{this.formatAmount(row.get('brutto'))}</td>
				<td className="right">{this.formatAmount(row.get('netto'))}</td>
			</tr>
		);
		if (kids && expanded) {
			const subType = row.get('subType');
			if (subType) {
				res.push(
					<tr className="noHover" key={'children_' + id}>
						<td colSpan={level + 1} className="dummy" />
						<td className="subHeader" />
						<td colSpan={8 - level - 2} className="subHeader">{getPhrase('dashboard.subHeader.' + subType)}</td>
					</tr>
				);
			}
			kids.forEach(kid => {
				this.appendRow(res, kid, false, level + 1, expandedRows);
			});
		}
	}

	renderRows(rows, expandedRows) {
		let res = [];
		rows.forEach((row, index) => {
			this.appendRow(res, row, index & 1, 0, expandedRows);
		});
		return res;
	}

	renderNoData() {
		return (
			<Message>
				{getPhrase('dashboardNoDataMessage')}
			</Message>
		);
	}

	formatSummaryValue(name, value) {
		if (!value) {
			return null;
		}
		if (name === 'totalNettoReward') {
			return (
				<span className="big">
					{this.formatWholeNumber(Math.floor(value))}<span className="small">{this.formatAmount(value - Math.floor(value)).substring(1) + ' ' + getPhrase('czk')}</span>
				</span>
			);
		} else {
			return value;
		}
	}

	renderColumnHeader(name, colSpan, className, filterInput, sortColumn, sortDesc) {
		let sortIndex = sortColumn === name ? 1 : 0;
		if (sortIndex) {
			if (sortDesc) {
				sortIndex = -sortIndex;
			}
		}
		return (
			<th colSpan={colSpan} className={'sortable ' + (className || '')}>
				<div
					className="text"
					onMouseDown={e => {
						e.preventDefault();
						this.orderBy(name, sortIndex > 0);
						//this.props.onSortRequested(col.get('name'), !e.ctrlKey);
					}}
				>
					{getColumnText(name, 'dashboard')}
					<span className="sortIcons">
						{
							sortIndex >= 0 &&
							<Icon
								name="sortAsc"
								className={sortIndex > 0 ? 'selected' : ''}
							/>

						}
						{
							sortIndex <= 0 &&
							<Icon
								name="sortDesc"
								className={sortIndex < 0 ? 'selected' : ''}
							/>

						}
					</span>
				</div>
				<Filter>
					{filterInput}
				</Filter>
			</th>
		);
	}

	renderColumnHeaderRow() {
		const model = this.getModel();
		const filter = model.getFilter();
		const distTypeLookup = getLookup('distType');
		const effectivePeriodLookup = model.getEffectivePeriodLookup();
		const sortColumn = model.getSortColumn();
		const sortDesc = model.getSortDesc();
		return (
			<tr className="sticky">
				{this.renderColumnHeader('categoryName', 4, null,
					<TextInput2
						changeOnBlur
						withClearButton
						rightIcon="search"
						value={filter.get('categoryName')}
						onChange={value => this.setFilterItem('categoryName', value)}
					/>, sortColumn, sortDesc
				)}
				{this.renderColumnHeader('effectivePeriodId', 1, null,
					<LookupInput
						getText={effectivePeriodLookup.getText}
						getRows={effectivePeriodLookup.getRows}
						combo
						value={filter.get('effectivePeriodId')}
						onChange={value => this.setFilterItem('effectivePeriodId', value)}
					/>, sortColumn, sortDesc
				)}
				{this.renderColumnHeader('distType', 1, null,
					<LookupInput
						getText={distTypeLookup.getText}
						getRows={distTypeLookup.getRows}
						combo
						value={filter.get('distType')}
						onChange={value => this.setFilterItem('distType', value)}
					/>, sortColumn, sortDesc
				)}
				{this.renderColumnHeader('brutto', 1, null,
					<PopupInput
						value={filter.get('brutto')}
						text={filter.getIn(['brutto', 'text'])}
						onChange={value => this.setFilterItem('brutto', value)}
						popupControl={NumberRangeInput}
						popupControlProps={{
							placeholderFrom: getColumnText('from'),
							placeholderTo: getColumnText('to'),
							inputSize: 10,
						}}
					/>, sortColumn, sortDesc
				)}
				{this.renderColumnHeader('netto', 1, null,
					<PopupInput
						value={filter.get('netto')}
						text={filter.getIn(['netto', 'text'])}
						onChange={value => this.setFilterItem('netto', value)}
						popupControl={NumberRangeInput}
						popupControlProps={{
							placeholderFrom: getColumnText('from'),
							placeholderTo: getColumnText('to'),
							inputSize: 10,
						}}
					/>, sortColumn, sortDesc
				)}
			</tr>
		);
	}

	render() {
		const model = this.getModel();
		const summary = model.getSummary();
		const filteredSummary = model.getFilteredSummary();
		const rows = model.getFilteredRows();
		const roleId = model.getRoleId();
		const roleLookup = model.getRoleLookup();
		const dpLookup = model.getDistributionPeriodLookup();
		const languageTimestamp = model.getLanguageTimestamp();
		if (!roleLookup) {
			return null;
		}
		if (roleLookup.isEmpty()) {
			return this.renderNoData();
		}

		// console.log('rows = ', rows.toJS());
		return (
			<Wrapper>
				<ModuleHeaderSection>
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('Dashboard')}</ModuleHeader>
						<Form virtual horizontal>
							<FormItem label={getColumnText('party')} fixedWidth="470px" marginRight="30px">
								<LookupInput
									getText={roleLookup.getText}
									getRows={roleLookup.getRows}
									combo
									mandatory
									value={roleId}
									onChange={this.setRoleId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
							<FormItem label={getColumnText('distribution_period')} fixedWidth="190px">
								<LookupInput
									getText={dpLookup.getText}
									getRows={dpLookup.getRows}
									combo
									mandatory
									value={model.getDistributionPeriodId()}
									onChange={this.setDistributionPeriodId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
						</Form>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock>
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				<ModuleBodySection>
					<SummaryBlock>
						{
							summaryItems.map(itemName => {
								return (
									<SummaryItem key={itemName}>
										<div className="label">{getColumnText(itemName)}</div>
										<div className="value">{this.formatSummaryValue(itemName, summary.get(itemName))}</div>
									</SummaryItem>
								);
							})
						}
					</SummaryBlock>
					<SmartTable>
						<colgroup>
							<col width="44px" />
							<col width="44px" />
							<col width="44px" />
							<col width="*" />
							<col width="210px" />
							<col width="170px" />
							<col width="170px" />
							<col width="170px" />
						</colgroup>
						<thead>
							{this.renderColumnHeaderRow()}
							<tr className="summary">
								<th colSpan="6" />
								<th className="right">
									<CellMainText>{this.formatAmount(filteredSummary.get('brutto') ?? summary.get('totalBrutto'))}</CellMainText>
									<div className="subText">{getPhrase('czk')} ({getColumnText('brutto')})</div>
								</th>
								<th className="right">
									<CellMainText>{this.formatAmount(filteredSummary.get('netto') ?? summary.get('totalNetto'))}</CellMainText>
									<div className="subText">{getPhrase('czk')} ({getColumnText('netto')})</div>
								</th>
							</tr>
							<tr className="dummyHeaderRow">
								<th colSpan="8">
								</th>
							</tr>
						</thead>
						<tbody>
							{this.renderRows(rows, model.getExpandedRows())}
						</tbody>
					</SmartTable>
				</ModuleBodySection>
			</Wrapper>
		);
	}
}

const summaryItems = Immutable.List([
	'totalNettoReward',
	'highestRewardCategory',
	'topChannel',
	//	'mostProfitableRecording'
]);

DashboardModule.contextType = GlobalContext;

export default withGlobalContext(DashboardModule);
